import { useState, useEffect } from 'react';
import {
  retrieveFromSessionStorage,
  saveInSessionStorage,
} from '../utils/storage';
import dayjs, { Dayjs } from 'dayjs';
import { DAYJS_DATA_DATE_FORMAT } from '../utils/date';
import {
  AllowedCalendarRangeTypes,
  AllowedCalendarView,
  SessionCalendarState,
} from '../types/core';

const SESSION_CALENDAR_KEY = 'calendar-state';

interface StoredSessionCalendarState {
  selectedContractId: string | null;
  selectedDate: string;
  selectedView: AllowedCalendarView;
}

export interface InitialCalendarState {
  selectedContractId?: string;
  selectedDate?: Dayjs;
  selectedView?: AllowedCalendarView;
}

const useSessionCalendarState = (
  sessionKey: string,
  rangeType: AllowedCalendarRangeTypes,
  initialState?: InitialCalendarState,
) => {
  const [calendarState, setCalendarState] = useState<SessionCalendarState>(
    () => {
      const storedSessionData =
        retrieveFromSessionStorage<StoredSessionCalendarState>(
          [SESSION_CALENDAR_KEY, sessionKey].join('-'),
          {
            checkDataType: (data): data is StoredSessionCalendarState =>
              Boolean(
                data &&
                  typeof data === 'object' &&
                  ((data as SessionCalendarState).selectedContractId === null ||
                    typeof (data as SessionCalendarState).selectedContractId ===
                      'string') &&
                  typeof (data as SessionCalendarState).selectedDate ===
                    'string' &&
                  Object.values(AllowedCalendarView).includes(
                    (data as SessionCalendarState).selectedView,
                  ),
              ),
          },
        );

      if (!storedSessionData) {
        return {
          selectedContractId: initialState?.selectedContractId ?? null,
          selectedDate: initialState?.selectedDate?.isValid()
            ? initialState.selectedDate
            : dayjs(new Date()),
          selectedView: ensureProperViewForRangeType(
            rangeType,
            initialState?.selectedView,
          ),
        };
      }

      const {
        selectedContractId,
        selectedDate: storedSelectedDate,
        selectedView,
      } = storedSessionData;

      const selectedDayObject = dayjs(storedSelectedDate);

      return {
        selectedContractId:
          initialState?.selectedContractId ?? selectedContractId,
        selectedDate: initialState?.selectedDate?.isValid()
          ? initialState.selectedDate
          : selectedDayObject.isValid()
            ? selectedDayObject
            : dayjs(new Date()),
        selectedView: ensureProperViewForRangeType(
          rangeType,
          initialState?.selectedView ?? selectedView,
        ),
      };
    },
  );

  useEffect(() => {
    const { selectedContractId, selectedDate, selectedView } = calendarState;
    saveInSessionStorage<StoredSessionCalendarState>(
      [SESSION_CALENDAR_KEY, sessionKey].join('-'),
      {
        selectedContractId,
        selectedDate: selectedDate.format(DAYJS_DATA_DATE_FORMAT),
        selectedView,
      },
    );
  }, [calendarState, sessionKey]);

  return { calendarState, setCalendarState };
};

export default useSessionCalendarState;

export const ensureProperViewForRangeType = (
  rangeType: AllowedCalendarRangeTypes,
  view?: AllowedCalendarView,
): AllowedCalendarView => {
  if (!view) {
    return rangeType === AllowedCalendarRangeTypes.Monthly
      ? AllowedCalendarView.ListMonth
      : AllowedCalendarView.ListDay;
  }

  if (
    rangeType === AllowedCalendarRangeTypes.Monthly &&
    ![AllowedCalendarView.ListMonth, AllowedCalendarView.DayGridMonth].includes(
      view,
    )
  ) {
    return AllowedCalendarView.ListMonth;
  } else if (
    rangeType === AllowedCalendarRangeTypes.Daily &&
    ![AllowedCalendarView.ListDay].includes(view)
  ) {
    return AllowedCalendarView.ListDay;
  }
  return view;
};
